<template>
    <div class="container-fluid">
        <!-- TITLE -->
        <div class="mt-2 tabName">
            <h3>{{ $t('reservations') }}</h3>
        </div>
        <!-- CREATE RESERVATION BUTTON -->
        <div class="row mt-3">
            <div class="col-sm-8">
                <router-link :to="{ name: 'reservation.create' }" class="btn btn-md btn-success">
                    {{ $t('create_reservation') }}
                </router-link>
            </div>
        </div>
        <!-- INFO TEST -->
        <div class="row mt-3" v-if="reservations.length > 0">
            <div class="col-sm-12 mt-3">{{ $t('reservation_list_info') }}</div>
        </div>
        <!-- RESERVATIONS TABLE -->
        <table class="table table-borderless mt-4" v-if="reservations.length">
            <thead>
                <tr :style="getBorderedRowStyle(true, true)">
                    <th class="align-middle">{{ $t('client') }}</th>
                    <th class="align-middle">{{ $t('pin') }}</th>
                    <th class="align-middle">{{ $t('medication_name') }}</th>
                    <th class="align-middle text-right">{{ $t('amount') }}</th>
                    <th class="align-middle">{{ $t('doctor') }}</th>
                    <th class="align-middle">{{ $t('prescription') }}</th>
                    <th class="align-middle">{{ $t('created_at') }}</th>
                    <th />
                </tr>
            </thead>
            <tbody>
                <template v-for="res in reservations">
                    <!-- for each reservation, each reserved and pre-reserved med is on its own row -->
                    <!-- first row -->
                    <tr
                        :key="`${res.res_id}_0`"
                        :style="getResRowCount(res) === 1 ? getBorderedRowStyle(false, true) : {}"
                    >
                        <td class="align-middle" :rowspan="getResRowCount(res)">
                            {{ res.client }}
                        </td>
                        <td class="align-middle" :rowspan="getResRowCount(res)">
                            {{ res.pin }}
                        </td>
                        <!-- print first reserved med on first row -->
                        <template v-for="med in getResMedRows(res, true)">
                            <td class="align-middle" :style="med.cellStyle" :key="`${med.vnr}_0_name`">
                                {{ getMedDisplayName(med) }}
                            </td>
                            <td class="align-middle text-right" :style="med.cellStyle" :key="`${med.vnr}_0_amount`">
                                {{ getMedDisplayAmount(med.amount, med.pills_amount, true) }}
                            </td>
                            <td class="align-middle" :style="med.cellStyle" :key="`${med.vnr}_0_doctor`">
                                {{ med.doctor }}
                            </td>
                            <td class="align-middle" :style="med.cellStyle" :key="`${med.vnr}_0_prescription`">
                                {{ med.prescription }}
                            </td>
                        </template>
                        <td class="align-middle" :rowspan="getResRowCount(res)">
                            {{ getLocalDate(res.created_at, $i18n.locale, true, true) }}
                        </td>
                        <td class="align-middle" :rowspan="getResRowCount(res)">
                            <div class="d-flex">
                                <button
                                    class="btn btn-sm btn-info ml-0 mr-3"
                                    v-on:click="() => print(res.res_id)"
                                    :disabled="submitClicked"
                                >
                                    {{ $t('print') }}
                                </button>
                                <div
                                    v-b-tooltip="{
                                        title: $t('error_reservation_locked'),
                                        disabled: !res.locked_until
                                    }"
                                >
                                    <button
                                        class="btn btn-sm btn-info mr-3"
                                        v-on:click="
                                            $router.push({ name: 'reservation.edit', params: { res_id: res.res_id } })
                                        "
                                        :disabled="submitClicked || res.locked_until"
                                    >
                                        {{ $t('edit') }}
                                    </button>
                                </div>
                                <div
                                    v-b-tooltip="{
                                        title: $t('error_reservation_locked'),
                                        disabled: !res.locked_until
                                    }"
                                >
                                    <button
                                        class="btn btn-sm btn-danger"
                                        v-on:click="() => handleDeleteReservationClick(res.res_id)"
                                        :disabled="submitClicked || res.locked_until"
                                    >
                                        {{ $t('delete') }}
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <!-- subsequent rows -->
                    <template v-for="med in getResMedRows(res, false)">
                        <tr
                            :key="`${res.res_id}_${med.vnr}_${med.preReserved ? 'pre' : ''}`"
                            :style="med.lastRow ? getBorderedRowStyle(false, true) : {}"
                        >
                            <td class="align-middle" :style="med.cellStyle">{{ getMedDisplayName(med) }}</td>
                            <td class="align-middle text-right" :style="med.cellStyle">
                                {{ getMedDisplayAmount(med.amount, med.pills_amount, true) }}
                            </td>
                            <td class="align-middle" :style="med.cellStyle">{{ med.doctor }}</td>
                            <td class="align-middle" :style="med.cellStyle">{{ med.prescription }}</td>
                        </tr>
                    </template>
                </template>
            </tbody>
        </table>
        <!-- MODAL: ARE YOU SURE YOU WANT TO DELETE RESERVATION? -->
        <b-modal id="deleteReservationModal" centered :title="$t('delete_reservation_are_you_sure_title')" hide-footer>
            <div class="row">
                <div class="col">
                    <p>{{ $t('delete_reservation_are_you_sure_text') }}</p>
                </div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="deleteReservation">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleDeleteReservationCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { deleteReservation, getReservations, handleAxiosError } from '@/services/axiosService';
import { getLocalDate } from '@/services/dateService';
import { getMedDisplayAmount, getMedDisplayName } from '@/services/medicationService';
import { combineReservations, printSticker } from '@/services/reservationService';

export default {
    data() {
        return {
            deleteReservationVnr: null,
            getLocalDate,
            getMedDisplayAmount,
            getMedDisplayName,
            loaded: false,
            reservations: [],
            reservationsDb: [],
            submitClicked: false
        };
    },
    async created() {
        // get reservations
        await this.getReservations();
        this.loaded = true;
    },
    computed: {
        amountTooltipText() {
            return `${this.$t('packages_pieces_tooltip_intro')} ${this.$t('packages_pieces_tooltip').toLowerCase()}`;
        }
    },
    methods: {
        // delete reservation
        async deleteReservation() {
            try {
                this.$bvModal.hide('deleteReservationModal');
                await deleteReservation(this.deleteReservationVnr);
                this.$toastr.s(this.$t('reservation_deleted'));
                await this.getReservations();
            } catch (error) {
                handleAxiosError(error);
            }
            this.handleDeleteReservationCancel();
        },
        // construct table row style object based on input params
        getBorderedRowStyle(top, bottom) {
            return {
                borderStyle: `${top ? 'solid' : 'none'} none ${bottom ? 'solid' : 'none'} none`,
                borderWidth: '1px',
                borderColor: 'lightgrey'
            };
        },
        // get reservations
        async getReservations() {
            try {
                this.reservationsDb = await getReservations();
                // format reservation array (combine reservation objects with same res_id)
                this.reservations = this.reservationsDb.reduce((acc, res) => {
                    let index = acc.findIndex(r => r.res_id === res.res_id);
                    if (index === -1) {
                        acc.push({
                            ...res,
                            ids: [],
                            medications: [],
                            medications_pre: []
                        });
                        index = acc.length - 1;
                        delete acc[index].id;
                    }
                    acc[index].ids.push(res.id);
                    if (res.pre_reserved) {
                        acc[index].medications_pre.push(...res.medications);
                    } else {
                        acc[index].medications.push(...res.medications);
                    }
                    return acc;
                }, []);
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // split reservation into rows based on its medications
        getResMedRows(res, firstOnly) {
            let medRows = [];

            const rowCount = this.getResRowCount(res);

            const addRow = (med, preReserved) => {
                return {
                    ...med,
                    cellStyle: preReserved ? { backgroundColor: 'pink' } : {},
                    preReserved
                };
            };

            if (firstOnly) {
                medRows.push(
                    res.medications && res.medications.length
                        ? addRow(res.medications[0], false)
                        : addRow(res.medications_pre[0], true)
                );
                if (rowCount === 1) {
                    medRows[0].lastRow = true;
                }
            } else {
                if (res.medications && res.medications.length) {
                    medRows.push(...res.medications.slice(1).map(med => addRow(med, false)));
                    if (res.medications_pre && res.medications_pre.length) {
                        medRows.push(...res.medications_pre.map(med => addRow(med, true)));
                    }
                } else {
                    medRows.push(...res.medications_pre.slice(1).map(med => addRow(med, true)));
                }
                if (medRows.length) {
                    medRows[medRows.length - 1].lastRow = true;
                }
            }
            return medRows;
        },
        // get reservation row count (based on its medications)
        getResRowCount(res) {
            let rowCount = 0;
            rowCount += (res.medications && res.medications.length) || 0;
            rowCount += (res.medications_pre && res.medications_pre.length) || 0;
            return rowCount;
        },
        // handle "delete reservation" cancel
        handleDeleteReservationCancel() {
            this.submitClicked = false;
            this.deleteReservationVnr = null;
            this.$bvModal.hide('deleteReservationModal'); // hide modal
        },
        // handle "delete reservation" button click
        handleDeleteReservationClick(vnr) {
            this.submitClicked = true;
            this.deleteReservationVnr = vnr;
            this.$bvModal.show('deleteReservationModal'); // show modal
        },
        // print reservation sticker
        print(res_id) {
            const reservation = combineReservations(this.reservationsDb.filter(res => res.res_id === res_id));
            printSticker(reservation);
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.handleDeleteReservationCancel();
            }
        });
    }
};
</script>
